import React, {
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import MapboxView from './MapboxView';
import projectApi from '../../utils/api/project';
import { getDownloadUrl } from '../../utils/api/upload';
import Loading from '../Shared/ResourceIndex/Loading';

const toCoord = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);

  return Number.isFinite(n) ? n : null;
};

/**
 * GIS Calibration UI — lets the user align the Mapbox satellite map with the
 * uploaded planset image so the four image corners can be saved as GPS coords.
 */
const GISCalibration = forwardRef(function GISCalibration(
  { project, projectUpload, onSaved, onCancel, onReadyChange, bounds = null },
  ref,
) {
  const mapRef = useRef(null);
  const imageRef = useRef(null);
  const tooltipRef = useRef(null);
  const mapReadyRef = useRef(false);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [initialCenter, setInitialCenter] = useState(null);

  const projectLat = toCoord(project?.address?.latitude);
  const projectLng = toCoord(project?.address?.longitude);
  const imageUrl = projectUpload?.upload?.GUID
    ? getDownloadUrl(projectUpload.upload.GUID)
    : null;

  useEffect(() => {
    const pu = projectUpload;
    if (
      pu?.geo_top_left_lat != null &&
      pu?.geo_top_right_lat != null &&
      pu?.geo_bottom_left_lat != null &&
      pu?.geo_bottom_right_lat != null
    ) {
      setInitialCenter([
        (pu.geo_top_left_lng +
          pu.geo_top_right_lng +
          pu.geo_bottom_left_lng +
          pu.geo_bottom_right_lng) /
          4,
        (pu.geo_top_left_lat +
          pu.geo_top_right_lat +
          pu.geo_bottom_left_lat +
          pu.geo_bottom_right_lat) /
          4,
      ]);
    } else if (projectLat != null && projectLng != null) {
      setInitialCenter([projectLng, projectLat]);
    }
  }, [projectUpload, projectLat, projectLng]);

  // Expose save() to parent so the Toolbar can trigger it headlessly.
  useImperativeHandle(ref, () => ({ save: handleSave })); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (onReadyChange) onReadyChange(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns the actual pixel rect of the rendered image when object-fit: contain
  // letterboxes it inside its container.
  function getContainedImageRect(containerW, containerH, naturalW, naturalH) {
    const imgAspect = naturalW / naturalH;
    const containerAspect = containerW / containerH;
    let renderedW, renderedH;
    if (imgAspect > containerAspect) {
      renderedW = containerW;
      renderedH = containerW / imgAspect;
    } else {
      renderedH = containerH;
      renderedW = containerH * imgAspect;
    }
    return {
      x: (containerW - renderedW) / 2,
      y: (containerH - renderedH) / 2,
      w: renderedW,
      h: renderedH,
    };
  }

  // Computes a Mapbox camera that re-aligns the satellite map with the
  // previously saved GPS corners, so reopening the calibration UI looks the same
  // regardless of screen size. Uses cameraForBounds with letterbox padding so
  // the geographic bounds always fit exactly within the rendered image area.
  function computeAlignedView(map, img) {
    const pu = projectUpload;
    if (
      pu?.geo_top_left_lat == null ||
      pu?.geo_top_right_lat == null ||
      pu?.geo_bottom_left_lat == null ||
      pu?.geo_bottom_right_lat == null
    )
      return null;

    const containerRect = map.getContainer().getBoundingClientRect();
    const { x, y, w, h } = getContainedImageRect(
      containerRect.width,
      containerRect.height,
      img.naturalWidth,
      img.naturalHeight,
    );

    if (w === 0 || h === 0) return null;

    const lngs = [
      pu.geo_top_left_lng,
      pu.geo_top_right_lng,
      pu.geo_bottom_left_lng,
      pu.geo_bottom_right_lng,
    ];
    const lats = [
      pu.geo_top_left_lat,
      pu.geo_top_right_lat,
      pu.geo_bottom_left_lat,
      pu.geo_bottom_right_lat,
    ];
    const sw = [Math.min(...lngs), Math.min(...lats)];
    const ne = [Math.max(...lngs), Math.max(...lats)];

    // Padding accounts for letterboxing so the geographic bounds fit exactly
    // within the rendered image area, not the full container.
    const padding = {
      top: y,
      bottom: containerRect.height - y - h,
      left: x,
      right: containerRect.width - x - w,
    };

    return map.cameraForBounds([sw, ne], { padding });
  }

  // Aligns the map to stored corners if they exist; falls back to project bounds.
  // Called once both the map and the image are ready (either can win the race).
  function tryAlignStoredCorners(map) {
    const img = imageRef.current;
    if (!img?.naturalWidth) return;

    const camera = computeAlignedView(map, img);
    if (camera) {
      map.jumpTo(camera);
    } else if (bounds?.west != null) {
      map.fitBounds(
        [
          [bounds.west, bounds.south],
          [bounds.east, bounds.north],
        ],
        { padding: 40, animate: false },
      );
    }
    setReady(true);
    if (onReadyChange) onReadyChange(true);
  }

  const handleMapLoad = useCallback((map) => {
    mapReadyRef.current = true;

    // Coordinate tooltip — updated via DOM ref to avoid React re-renders on mousemove.
    map.on('mousemove', (e) => {
      const el = tooltipRef.current;
      if (!el) return;
      el.style.display = 'block';
      el.style.left = `${e.point.x + 14}px`;
      el.style.top = `${e.point.y + 14}px`;
      el.textContent = `${e.lngLat.lat.toFixed(6)}, ${e.lngLat.lng.toFixed(6)}`;
    });
    const hideTooltip = () => {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    };
    map.on('mouseleave', hideTooltip);
    map.getContainer().addEventListener('mouseleave', hideTooltip);

    if (imageRef.current?.complete && imageRef.current?.naturalWidth) {
      tryAlignStoredCorners(map);
    }

    setMapLoaded(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageLoad = useCallback(() => {
    if (!mapReadyRef.current) return;
    const map = mapRef.current?.getMap();
    if (map) tryAlignStoredCorners(map);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!mapLoaded) return;
    const map = mapRef.current?.getMap();
    if (!map || !imageRef.current) return;

    setSaving(true);

    try {
      const mapContainer = map.getContainer();
      const containerRect = mapContainer.getBoundingClientRect();
      const img = imageRef.current;

      const { x, y, w, h } = getContainedImageRect(
        containerRect.width,
        containerRect.height,
        img.naturalWidth,
        img.naturalHeight,
      );

      // Unproject image corners from screen pixels → GPS at 6-decimal precision.
      const topLeft = map.unproject([x, y]);
      const topRight = map.unproject([x + w, y]);
      const bottomRight = map.unproject([x + w, y + h]);
      const bottomLeft = map.unproject([x, y + h]);

      const updated = await projectApi.saveGeoreference(
        project.id,
        projectUpload.id,
        {
          geo_top_left_lat: topLeft.lat,
          geo_top_left_lng: topLeft.lng,
          geo_top_right_lat: topRight.lat,
          geo_top_right_lng: topRight.lng,
          geo_bottom_left_lat: bottomLeft.lat,
          geo_bottom_left_lng: bottomLeft.lng,
          geo_bottom_right_lat: bottomRight.lat,
          geo_bottom_right_lng: bottomRight.lng,
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
      );
      onSaved && onSaved(updated);
    } catch (err) {
      console.error(
        err?.message || 'Failed to save georeferencing. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!imageUrl) {
    return (
      <div className='gis-calibration gis-calibration--error'>
        <p>No image found for this site map. Please upload a planset first.</p>
        <button className='button' onClick={onCancel}>
          Close
        </button>
      </div>
    );
  }

  if (!initialCenter) {
    return (
      <div className='gis-calibration gis-calibration--error'>
        <p>
          This project does not have GPS coordinates set. Please update the
          project address with a valid latitude and longitude before
          calibrating.
        </p>
        <button className='button' onClick={onCancel}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      className='gis-calibration'
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <div
        className='gis-calibration__map'
        style={{ position: 'relative', flex: 1 }}
      >
        {/* Loader overlay — visible until map is loaded and aligned */}
        {!ready && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
            }}
          >
            <Loading what='map' />
          </div>
        )}

        {/* Satellite map — pans freely behind the fixed image */}
        <div
          style={{ position: 'absolute', inset: 0, opacity: ready ? 0.8 : 0 }}
        >
          <MapboxView
            ref={mapRef}
            onMapLoad={handleMapLoad}
            center={initialCenter}
            style='mapbox://styles/mapbox/satellite-streets-v12'
            initialOpacity={0}
            maxBounds={bounds}
          />
        </div>

        {/* Live coordinate tooltip — updated via DOM ref, no React re-renders */}
        <div
          ref={tooltipRef}
          style={{
            display: 'none',
            position: 'absolute',
            zIndex: 10,
            background: 'rgba(0,0,0,0.65)',
            color: '#fff',
            fontSize: 11,
            fontFamily: 'monospace',
            padding: '3px 7px',
            borderRadius: 4,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            lineHeight: 1.5,
          }}
        />

        {/* Planset image — fixed overlay so the user can pan the map beneath it */}
        <img
          ref={imageRef}
          src={imageUrl}
          alt='Planset overlay'
          onLoad={handleImageLoad}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: ready ? 0.55 : 0,
            pointerEvents: 'none',
            zIndex: 1,
            userSelect: 'none',
          }}
        />
      </div>
    </div>
  );
});

export default GISCalibration;
